import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { X, Pause, Play, SkipBack, SkipForward, Volume2, VolumeX, Trophy, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { buildTimeline, fmt } from "../lib/workoutEngine";

// ---------- Bunny player helpers ----------
// The workout timeline is the authoritative clock. Bunny videos are visual coaching only:
// master clips are ~59s (longer than any 30/40/45s work interval) so they play ONCE from
// 0:00 and never loop; the NEXT step's clip is prebuffered in a hidden iframe for instant transitions.
const playerCmd = (iframe, method, value) => {
  if (!iframe || !iframe.contentWindow) return;
  const msg = { context: "player.js", version: "0.0.11", method };
  if (value !== undefined) msg.value = value;
  iframe.contentWindow.postMessage(JSON.stringify(msg), "*");
};

const bunnySrc = (url, autoplay) =>
  `${url}${url.includes("?") ? "&" : "?"}preload=true&muted=true&autoplay=${autoplay ? "true" : "false"}`;

function VideoStage({ current, next, muted, playing, onFirstPlay, onPlaybackTimeout, registerResume }) {
  const refs = useRef({});    // url -> iframe element
  const seenPlay = useRef({}); // url -> true once actual playback is observed
  const firstFired = useRef(false);
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  // player.js events from the Bunny iframes: 'ready' = API up, 'play'/'timeupdate' = media ACTUALLY playing
  useEffect(() => {
    const onMsg = (e) => {
      let d;
      try { d = typeof e.data === "string" ? JSON.parse(e.data) : e.data; } catch { return; }
      if (!d || d.context !== "player.js") return;
      const f = Object.values(refs.current).find((el) => el && e.source === el.contentWindow);
      if (!f) return;
      if (d.event === "play" || d.event === "timeupdate") {
        seenPlay.current[f.dataset.url] = true;
        if (f.dataset.active === "1") {
          // playback CONFIRMED -> now safe to apply sound (iOS rejects unmuted gesture-less play, allows unmuting a playing clip)
          playerCmd(f, mutedRef.current ? "mute" : "unmute");
          if (!firstFired.current) {
            firstFired.current = true;
            onFirstPlay && onFirstPlay();  // media confirmed playing -> parent may start the workout clock
          }
        }
      } else if (d.event === "ready" && f.dataset.active === "1" && !seenPlay.current[f.dataset.url]) {
        playerCmd(f, "addEventListener", "play");
        playerCmd(f, "addEventListener", "timeupdate");
        playerCmd(f, "setCurrentTime", 0);   // only while never-yet-played (player boot at activation)
        playerCmd(f, "play");
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [onFirstPlay]);

  // expose a gesture-driven resume: the "Tap to continue" button calls this inside a real user gesture
  useEffect(() => {
    if (!registerResume) return;
    registerResume.current = () => {
      const f = current && refs.current[current.url];
      if (!f) return;
      playerCmd(f, "play");
      playerCmd(f, mutedRef.current ? "mute" : "unmute");
    };
  });

  // activate/deactivate the current clip at step boundaries.
  // playing=true (work/banner): seek-to-0 EXACTLY ONCE, then play (idempotent retries, no seeks).
  // playing=false (rest): one seek-to-0 + pause so the upcoming clip shows its FIRST FRAME, silent & still.
  useEffect(() => {
    Object.entries(refs.current).forEach(([k, f]) => {
      if (f && (!current || k !== current.url)) { f.dataset.active = "0"; playerCmd(f, "pause"); }
    });
    if (!current) return undefined;
    const f = refs.current[current.url];
    if (!f) return undefined;
    f.dataset.active = "1";
    if (!playing) {
      playerCmd(f, "pause");
      playerCmd(f, "setCurrentTime", 0);  // once per step boundary: park at first frame for the rest preview
      playerCmd(f, "pause");
      return undefined;
    }
    playerCmd(f, "addEventListener", "play");        // player.js events need explicit subscription
    playerCmd(f, "addEventListener", "timeupdate");
    playerCmd(f, "setCurrentTime", 0);               // ONCE: start this exercise's clip at 0:00
    playerCmd(f, "play");                            // player is muted via URL param -> iOS-safe
    playerCmd(f, "mute");                            // stay muted until playback is confirmed (unmute applied on play/timeupdate)
    let attempts = 0;
    let stallNotified = false;
    const iv = setInterval(() => {
      attempts += 1;
      if (seenPlay.current[current.url] || attempts > 12) { clearInterval(iv); return; }
      if (attempts === 4 && !stallNotified) {
        stallNotified = true;
        onPlaybackTimeout && onPlaybackTimeout();    // playback refused (e.g. iOS unmuted autoplay block) -> pause timer, offer tap-to-continue
      }
      playerCmd(f, "addEventListener", "play");
      playerCmd(f, "addEventListener", "timeupdate");
      playerCmd(f, "play");                          // muted retry: no-op once playing; never seeks
    }, 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current ? current.url : null, playing]);

  // live mute toggle on the active clip
  useEffect(() => {
    if (!current) return;
    const f = refs.current[current.url];
    if (f) [0, 300, 1000].forEach((ms) => setTimeout(() => playerCmd(f, muted ? "mute" : "unmute"), ms));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [muted]);

  if (!current || !current.url) {
    return <div className="w-full h-full grid place-items-center text-zinc-500 text-sm">Video not yet available</div>;
  }
  if (current.provider === "youtube") {
    return (
      <iframe
        key={`${current.url}-${muted}`}
        src={`https://www.youtube-nocookie.com/embed/${current.url}?autoplay=1&mute=${muted ? 1 : 0}&loop=1&playlist=${current.url}&controls=0&playsinline=1&rel=0&modestbranding=1`}
        title="Exercise" className="w-full h-full" allow="autoplay; encrypted-media; fullscreen" allowFullScreen
      />
    );
  }
  if (current.provider === "bunny") {
    // dual-buffer: current + next stay mounted; only visibility changes at the step boundary
    const vids = [current];
    if (next && next.provider === "bunny" && next.url && next.url !== current.url) vids.push(next);
    return (
      <>
        {vids.map((v) => (
          <iframe
            key={v.url}
            ref={(el) => { if (!el) return; if (refs.current[v.url] && refs.current[v.url] !== el) seenPlay.current[v.url] = false; refs.current[v.url] = el; }}
            data-active={v.url === current.url ? "1" : "0"}
            data-url={v.url}
            data-testid={v.url === current.url ? "exercise-video-frame" : "next-video-preload"}
            src={bunnySrc(v.url, false)}
            title="Exercise"
            className={`absolute inset-0 w-full h-full transition-opacity duration-150 ${v.url === current.url ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            allow="autoplay; fullscreen; encrypted-media"
            allowFullScreen
          />
        ))}
      </>
    );
  }
  return <video key={current.url} src={current.url} autoPlay muted={muted} loop playsInline className="w-full h-full object-contain" />;
}

export default function WorkoutSession() {
  const { eid, day, wid } = useParams();
  const preview = Boolean(wid);
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [error, setError] = useState(null);
  const [muted, setMuted] = useState(true);
  const [preparing, setPreparing] = useState(true);  // clock is gated until the first video is actually playing
  const [stalled, setStalled] = useState(false);     // playback refused mid-workout -> timer paused, tap-to-continue shown
  const [state, setState] = useState({ idx: 0, left: 0, elapsed: 0, paused: false, done: false });
  const resumePlayback = useRef(null);
  const lastTick = useRef(null);
  const steps = useMemo(() => (cfg ? buildTimeline(cfg) : []), [cfg]);
  const total = cfg?.total_seconds || 0;
  const backTo = preview ? "/admin" : `/app/programs/${eid}`;

  useEffect(() => {
    const url = preview ? `/admin/workout-sessions/${wid}/preview` : `/me/enrolments/${eid}/days/${day}/workout`;
    api.get(url)
      .then((r) => {
        setCfg(r.data);
        setState({ idx: 0, left: buildTimeline(r.data)[0]?.seconds || 0, elapsed: 0, paused: false, done: false });
      })
      .catch((e) => {
        const det = e.response?.data?.detail;
        if (e.response?.status === 401) { navigate("/login"); return; }
        setError(typeof det === "object" ? (det.message || "Locked") : (det || "Workout unavailable"));
      });
  }, [eid, day, wid, preview, navigate]);

  // main clock — timestamp-based, pause-aware; elapsed is derived from timeline position so skipping stays accurate.
  // GATED: does not start until the first video is confirmed playing (preparing=false).
  useEffect(() => {
    if (!cfg || state.done || preparing) return;
    lastTick.current = Date.now();
    const consumedBefore = (idx) => steps.slice(0, idx).reduce((a, s) => a + (s.seconds || 0), 0);
    const t = setInterval(() => {
      const now = Date.now();
      const dt = (now - lastTick.current) / 1000;
      lastTick.current = now;
      setState((s) => {
        if (s.paused || s.done) return s;
        let { idx, left } = s;
        left -= dt;
        while (left <= 0 && idx < steps.length - 1) {
          idx += 1;
          left += steps[idx].seconds || 0;
        }
        const elapsed = Math.min(total, consumedBefore(idx) + Math.max(0, (steps[idx]?.seconds || 0) - Math.max(0, left)));
        if (idx >= steps.length - 1 && left <= 0) return { ...s, idx, left: 0, elapsed: total, done: true };
        return { ...s, idx, left, elapsed };
      });
    }, 200);
    return () => clearInterval(t);
  }, [cfg, state.done, preparing, steps, total]);

  // first-video readiness: player.js confirms actual playback -> release the clock.
  // Safety watchdog only (rare failure path): never leave the customer stuck on the loader.
  const handleFirstPlay = useCallback(() => setPreparing(false), []);
  const handlePlaybackTimeout = useCallback(() => {
    setStalled(true);
    setState((s) => ({ ...s, paused: true }));   // timer must never run ahead of a stopped video
  }, []);
  const resumeFromStall = () => {
    resumePlayback.current && resumePlayback.current();  // real user gesture -> iOS allows play (and unmute if sound on)
    setStalled(false);
    setState((s) => ({ ...s, paused: false }));
  };
  useEffect(() => {
    if (!cfg || !preparing) return undefined;
    const wd = setTimeout(() => setPreparing(false), 15000);
    return () => clearTimeout(wd);
  }, [cfg, preparing]);

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0c0c0e] text-white px-6" data-testid="workout-error">
        <div className="text-center max-w-sm">
          <p className="font-display font-800 uppercase text-3xl tracking-tight mb-3">{error}</p>
          <p className="text-zinc-500 text-sm mb-8">{preview ? "This workout session could not be loaded." : "The guided workout for this day isn't available yet, or the day is still locked."}</p>
          <Link to={backTo} className="bg-primary text-primary-foreground px-8 py-3.5 rounded-full text-xs uppercase tracking-widest font-bold">{preview ? "Back to Admin" : "Back to Program"}</Link>
        </div>
      </div>
    );
  }
  if (!cfg) return <div className="min-h-screen grid place-items-center bg-[#0c0c0e] text-zinc-500">Loading workout…</div>;

  const step = steps[state.idx] || {};
  const finalFive = !state.paused && (step.kind === "work" || step.kind === "rest") && state.left <= 5 && state.left > 0;

  const jump = (dir) => {
    const ni = Math.min(Math.max(state.idx + dir, 0), steps.length - 1);
    setState((s) => ({ ...s, idx: ni, left: steps[ni].seconds || 0, done: ni === steps.length - 1 && (steps[ni].seconds || 0) <= 0 }));
    lastTick.current = Date.now();
  };
  const togglePause = () => { lastTick.current = Date.now(); setState((s) => ({ ...s, paused: !s.paused })); };
  const exit = () => { if (window.confirm("Exit workout? Progress for this session won't be saved.")) navigate(backTo); };
  const finish = async () => {
    if (preview) { navigate("/admin"); return; }
    try { await api.post(`/me/enrolments/${eid}/days/${day}/complete`); toast.success("Day marked complete! +10 pts"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    navigate(backTo);
  };

  const isRest = step.kind === "rest";
  const isBanner = step.kind === "banner";
  const progressPct = total ? Math.min(100, (state.elapsed / total) * 100) : 0;

  // video to show per step: work -> its own clip; rest/banner -> the UPCOMING exercise clip
  // (prebuffered, paused at 0, ready to play the instant the boundary hits)
  const displayVideo = (i) => {
    const s = steps[i];
    if (!s) return null;
    if (s.kind === "work") return s.video || null;
    const nw = steps.slice(i + 1).find((x) => x.kind === "work");
    return nw ? nw.video || null : null;
  };
  const currentVideo = displayVideo(state.idx);
  const nextVideo = displayVideo(state.idx + 1);

  // section-aware progress cards — never show empty "—" values
  const cards = (() => {
    if (step.kind === "work" && step.section === "Warm-up") return [["SECTION", "WARM-UP"], ["EXERCISE", `${step.secIndex} / ${step.secTotal}`]];
    if (step.kind === "work" && step.section === "Cool down") return [["SECTION", "COOL DOWN"], ["EXERCISE", `${step.secIndex} / ${step.secTotal}`]];
    if (step.kind === "work") return [["ROUND", `${step.round} / ${step.rounds}`], ["EXERCISE", `${step.exIndex} / ${step.exTotal}`]];
    if (step.kind === "rest") return [
      ["ROUND", step.label === "ROUND BREAK" ? `${step.round + 1} / ${step.rounds}` : `${step.round} / ${step.rounds}`],
      ["NEXT EXERCISE", step.nextEx || "Cool down"],
    ];
    return [["GET READY", step.label || "—"], ["UP NEXT", step.upNext || "Finish"]];
  })();

  // ---------- completion screen ----------
  if (state.done) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0c0c0e] text-white px-6" data-testid="workout-complete">
        <div className="text-center max-w-md w-full">
          <div className="w-20 h-20 rounded-full bg-primary/15 text-primary grid place-items-center mx-auto mb-6"><Trophy size={36} /></div>
          <h1 className="font-display font-900 uppercase text-5xl tracking-tight">Workout Complete</h1>
          <p className="text-zinc-400 mt-3">Strong work. See you tomorrow.</p>
          <div className="grid grid-cols-2 gap-3 mt-8">
            {[["Day", cfg.day], ["Level", cfg.level], ["Duration", fmt(state.elapsed)], ["Exercises", cfg.exercise_count]].map(([l, v]) => (
              <div key={l} className="border border-white/10 rounded-xl bg-white/[0.04] p-4">
                <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">{l}</div>
                <div className="font-display font-800 text-2xl mt-1">{v}</div>
              </div>
            ))}
          </div>
          <button onClick={finish} data-testid="continue-btn" className="mt-8 w-full bg-primary text-primary-foreground py-4 rounded-full text-xs uppercase tracking-[0.15em] font-bold hover:scale-[1.02] transition-transform">
            Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white flex flex-col" data-testid="workout-player"
      style={{ background: "radial-gradient(1200px 600px at 50% -10%, #1c1c21 0%, #101013 45%, #0b0b0d 100%)" }}>
      {/* top bar */}
      <div className="px-4 md:px-8 pt-4">
        <div className="flex items-center justify-between gap-3 max-w-[1500px] mx-auto">
          <button onClick={exit} data-testid="exit-workout" className="flex items-center gap-2 text-zinc-400 hover:text-white text-xs uppercase tracking-widest font-bold"><X size={16} /> Exit</button>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500" data-testid="session-meta">Day {cfg.day} · {cfg.level}</div>
            <div className="font-display font-800 text-sm md:text-base tabular-nums" data-testid="workout-clock">{fmt(state.elapsed)} <span className="text-zinc-500">/ {fmt(total)}</span></div>
          </div>
          <button onClick={() => setMuted(!muted)} data-testid="sound-toggle" className="text-zinc-400 hover:text-white p-2" aria-label="Toggle sound">{muted ? <VolumeX size={18} /> : <Volume2 size={18} />}</button>
        </div>
        <div className="h-1 bg-white/10 rounded-full mt-3 max-w-[1500px] mx-auto overflow-hidden">
          <div className="h-full bg-primary rounded-full transition-[width] duration-300" style={{ width: `${progressPct}%` }} data-testid="workout-progress" />
        </div>
      </div>

      {/* main */}
      <div className="flex-1 grid lg:grid-cols-[1fr_360px] gap-4 md:gap-6 px-4 md:px-8 py-4 max-w-[1500px] mx-auto w-full min-h-0">
        {/* video hero — stage always mounted so the next clip prebuffers during banners/rests */}
        <div className="relative rounded-2xl overflow-hidden bg-black border border-white/10 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.8)] min-h-[240px] lg:min-h-0" data-testid="video-stage">
          <VideoStage current={currentVideo} next={nextVideo} muted={muted} playing={!isRest} onFirstPlay={handleFirstPlay} onPlaybackTimeout={handlePlaybackTimeout} registerResume={resumePlayback} />
          {stalled && (
            <div className="absolute inset-0 z-20 grid place-items-center bg-black/80 backdrop-blur-sm" data-testid="playback-stalled-overlay">
              <div className="text-center px-6">
                <div className="font-display font-900 uppercase text-3xl md:text-4xl tracking-tight">Video paused</div>
                <p className="text-zinc-400 text-sm mt-2">Your browser paused the video. Your workout timer is paused too.</p>
                <button onClick={resumeFromStall} data-testid="resume-playback-btn"
                  className="mt-5 bg-primary text-primary-foreground font-display font-900 uppercase tracking-widest text-sm px-8 py-3 rounded-full">
                  Tap to continue
                </button>
              </div>
            </div>
          )}
          {preparing && (
            <div className="absolute inset-0 z-10 grid place-items-center bg-[#0c0c0e]" data-testid="preparing-overlay">
              <div className="text-center">
                <div className="font-display font-900 uppercase text-5xl md:text-7xl tracking-tight text-primary">Get Ready</div>
                <div className="text-zinc-400 mt-4 text-sm uppercase tracking-widest flex items-center justify-center gap-2">
                  <span className="inline-block w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  Preparing your workout…
                </div>
                {currentVideo && <div className="text-zinc-600 mt-2 text-xs uppercase tracking-widest">First up · {step.upNext || steps.find((x) => x.kind === "work")?.name}</div>}
              </div>
            </div>
          )}
          {isBanner && !preparing && (
            <div className="absolute inset-0 grid place-items-center bg-[#0c0c0e]" data-testid="step-banner">
              <div className="text-center">
                <div className="font-display font-900 uppercase text-6xl md:text-8xl tracking-tight text-primary">{step.label}</div>
                {step.upNext && <div className="text-zinc-400 mt-4 text-sm uppercase tracking-widest">First up · {step.upNext}</div>}
              </div>
            </div>
          )}
          {!isBanner && (
            <>
              {isRest && (
                <div className="absolute inset-0 bg-black/70 backdrop-blur-sm grid place-items-center" data-testid="rest-overlay">
                  <div className="text-center">
                    <div className="text-primary text-xs uppercase tracking-[0.25em] font-bold">{step.label || "REST"}</div>
                    <div className={`font-display font-900 text-8xl md:text-9xl tabular-nums mt-2 ${finalFive ? "animate-pulse text-primary" : ""}`} data-testid="rest-timer">{fmt(state.left)}</div>
                    {step.upNext && <div className="text-zinc-400 mt-3 text-xs uppercase tracking-widest">Next · {step.upNext}</div>}
                  </div>
                </div>
              )}
              {state.paused && !stalled && (
                <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px] grid place-items-center">
                  <span className="font-display font-900 uppercase text-4xl tracking-tight">Paused</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* side / lower panel */}
        <div className="flex flex-col gap-4 min-h-0">
          <div className={`rounded-2xl border p-5 md:p-6 text-center transition-colors ${isRest ? "bg-primary text-primary-foreground border-primary" : "bg-white/[0.04] border-white/10"}`} data-testid="big-timer-card">
            {isBanner ? (
              <div className="font-display font-900 uppercase text-4xl tracking-tight py-4">{step.label}</div>
            ) : step.kind === "work" ? (
              <>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">{step.section}</div>
                <div className="font-display font-900 uppercase text-3xl md:text-4xl tracking-tight leading-none" data-testid="exercise-name">{step.name}</div>
                <div className={`font-display font-900 text-7xl md:text-8xl tabular-nums mt-3 ${finalFive ? "animate-pulse text-primary" : ""}`} data-testid="big-timer">{fmt(state.left)}</div>
                <div className="flex items-center justify-center gap-2 mt-2" data-testid="work-badges">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-300 bg-white/10 rounded-full px-3 py-1">{step.work_seconds} SEC</span>
                  {step.exercise_type === "STATIC" && <span className="text-[10px] uppercase tracking-widest text-primary bg-primary/10 rounded-full px-3 py-1">HOLD</span>}
                  {step.alternating && <span className="text-[10px] uppercase tracking-widest text-primary bg-primary/10 rounded-full px-3 py-1">Alternate legs</span>}
                </div>
              </>
            ) : (
              <>
                <div className="text-[10px] uppercase tracking-[0.25em] mb-1 opacity-80">{step.label || "REST"}</div>
                <div className="font-display font-900 text-7xl md:text-8xl tabular-nums" data-testid="big-timer">{fmt(state.left)}</div>
              </>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4 text-center" data-testid="round-indicator">
              <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">{cards[0][0]}</div>
              <div className="font-display font-800 text-2xl mt-1">{cards[0][1]}</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4 text-center" data-testid="exercise-counter">
              <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">{cards[1][0]}</div>
              <div className="font-display font-800 text-2xl mt-1">{cards[1][1]}</div>
            </div>
          </div>

          <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4 flex items-center gap-3" data-testid="next-exercise">
            <CheckCircle2 size={18} className="text-primary shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Up next</div>
              <div className="font-bold text-sm truncate">{step.upNext || "Finish"}</div>
            </div>
          </div>

          {/* controls */}
          <div className="mt-auto flex items-center justify-center gap-4 py-2">
            <button onClick={() => jump(-1)} data-testid="prev-step" className="w-12 h-12 rounded-full border border-white/15 grid place-items-center text-zinc-300 hover:border-primary hover:text-primary transition-colors" aria-label="Previous"><SkipBack size={18} /></button>
            <button onClick={togglePause} data-testid="pause-btn" className="w-16 h-16 rounded-full bg-primary text-primary-foreground grid place-items-center shadow-[0_10px_30px_-8px_rgba(255,87,34,0.6)] hover:scale-105 transition-transform" aria-label="Pause or resume">
              {state.paused ? <Play size={26} /> : <Pause size={26} />}
            </button>
            <button onClick={() => jump(1)} data-testid="next-step" className="w-12 h-12 rounded-full border border-white/15 grid place-items-center text-zinc-300 hover:border-primary hover:text-primary transition-colors" aria-label="Next"><SkipForward size={18} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
